import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { usePremiumCredits } from '@/hooks/usePremiumCredits';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Camera as CameraIcon, Image } from 'lucide-react';
import { toast } from 'sonner';
import { PremiumCreditsDisplay } from '@/components/PremiumCreditsDisplay';

const Camera = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hasPremium, creditsRemaining, loading: creditsLoading } = usePremiumCredits();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);

  useEffect(() => {
    const freeTrialMode = location.state?.freeTrialMode;

    if (!user && !freeTrialMode) {
      toast.error(t('camera.loginRequired'));
      navigate('/auth');
      return;
    }
    
    startCamera();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [user, location.state]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error(t('camera.cameraError'));
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg');
        setCapturedImage(imageData);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEvaluate = () => {
    if (capturedImage) {
      const freeTrialMode = location.state?.freeTrialMode;
      navigate('/result', { 
        state: { 
          image: capturedImage,
          freeTrialMode 
        } 
      });
    }
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-black">
      <div className="absolute top-0 left-0 right-0 z-10 p-4 flex items-center justify-between bg-gradient-to-b from-black/50 to-transparent">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="text-white hover:bg-white/20"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        
        {user && !location.state?.freeTrialMode && (
          <PremiumCreditsDisplay 
            hasPremium={hasPremium}
            creditsRemaining={creditsRemaining}
            loading={creditsLoading}
          />
        )}
      </div>

      <div className="h-full w-full">
        {capturedImage ? (
          <img
            src={capturedImage}
            alt="Captured"
            className="h-full w-full object-cover"
          />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="h-full w-full object-cover"
          />
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center justify-center gap-4">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className="hidden"
          />
          
          <Button
            size="lg"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-full h-14 w-14 p-0 border-2 border-white/50 bg-white/10 hover:bg-white/20"
          >
            <Image className="h-6 w-6 text-white" />
          </Button>

          {!capturedImage ? (
            <Button
              size="lg"
              onClick={capturePhoto}
              className="rounded-full h-20 w-20 p-0 bg-white hover:bg-white/90"
            >
              <CameraIcon className="h-8 w-8 text-black" />
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                size="lg"
                variant="outline"
                onClick={() => setCapturedImage(null)}
                className="border-2 border-white/50 bg-white/10 hover:bg-white/20 text-white"
              >
                {t('camera.retake')}
              </Button>
              <Button
                size="lg"
                onClick={handleEvaluate}
                className="bg-primary hover:bg-primary/90"
              >
                {t('camera.evaluate')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default Camera;
